import {
  CloseButton,
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';

// Import necessary icons
import ChevronLeftIcon from '../Icons/ChevronLeftIcon';
import CloseIcon from '../Icons/CloseIcon';

const DModal = ({
  children,
  title,
  subtitle,
  isOpen,
  onClose,
  footer,
  backBtn,
  onBack,
  hideCloseButton = false,
  closeBackdrop = true,
  imageSection = null,
  imageBgColor = 'bg-white', // Light blue background from reference image
  contentBgColor = 'bg-white',
  className = '',
  headerClassName = '',
  titleClassName = '',
  subtitleClassName = '',
  closeButtonClassName = '',
  line = true,
  footerClassName = '',
  contentGap = 'gap-size2',
  contentPadding = 'p-size5',
  position = 'center', // 'center' or 'bottom-right'
  positionRight = '24px', // right offset (default 6 = 24px)
  positionBottom = '24px', // bottom offset (default 6 = 24px)
}) => {
  const hasSideBySideLayout = !!imageSection;
  const isBottomRight = position === 'bottom-right';

  // For bottom-right modals, render outside Dialog to avoid focus trapping and blocking clicks
  if (isBottomRight && isOpen) {
    return (
      <div
        className="fixed flex pointer-events-none z-50"
        style={{
          right: positionRight,
          bottom: positionBottom
        }}
      >
        <div
          className={`${className} transition-all duration-300 pointer-events-auto rounded-2xl overflow-visible max-h-[90vh] flex flex-col border border-grey-5`}
        >
          {hasSideBySideLayout ? (
            <div className={'flex flex-col md:flex-row w-full h-full'}>
              {/* Image Section */}
              <div
                className={`${imageBgColor} md:w-2/5 flex items-center justify-center py-size3 md:py-size8 px-size5`}
              >
                {imageSection}
              </div>

              {/* Content Section */}
              <div
                className={`${contentBgColor} md:w-3/5 flex flex-col gap-size3 p-size3`}
              >
                <header
                  className={`flex items-start ${headerClassName} relative`}
                >
                  {backBtn && (
                    <button onClick={() => onBack()} className="w-[30px]">
                      <ChevronLeftIcon />
                    </button>
                  )}
                  <div
                    className={`flex flex-col gap-size1 sm:gap-size2 ${
                      title ? 'max-w-[85%] sm:max-w-[80%]' : 'max-w-[100%]'
                    }`}
                  >
                    {title && (
                      <div
                        className={`text-base sm:text-lg font-medium leading-tight sm:leading-snug ${titleClassName}`}
                      >
                        {title}
                      </div>
                    )}
                    {subtitle && (
                      <div
                        className={`text-xs sm:text-sm text-grey-50 w-full leading-relaxed ${subtitleClassName}`}
                      >
                        {subtitle}
                      </div>
                    )}
                  </div>
                  {!hideCloseButton && (
                    <button onClick={onClose} className="absolute right-0 top-[-2px]">
                      <CloseIcon width={24} height={24} />
                    </button>
                  )}
                </header>

                {title && line && (
                  <div className="w-full h-px bg-grey-5"></div>
                )}
                <main className="text-base grow overflow-y-auto overflow-x-hidden scrollbar">
                  {children}
                </main>
                {footer && (
                  <footer className="flex items-center gap-size1 w-full">
                    {footer}
                  </footer>
                )}
              </div>
            </div>
          ) : (
            // Original layout when no image section is provided
            <div
              className={`${contentBgColor} flex flex-col ${contentGap} ${contentPadding} w-full`}
            >
              <header
                className={`flex items-center ${headerClassName} relative`}
              >
                {backBtn && (
                  <button onClick={() => onBack()} className="w-[30px]">
                    <ChevronLeftIcon />
                  </button>
                )}
                <div
                  className={`flex flex-col gap-size1 sm:gap-size2 ${
                    title ? 'max-w-[85%] sm:max-w-[80%]' : 'max-w-[100%]'
                  }`}
                >
                  {title && (
                    <div
                      className={`text-base sm:text-lg font-medium leading-tight sm:leading-snug ${titleClassName}`}
                    >
                      {title}
                    </div>
                  )}
                  {subtitle && (
                    <div
                      className={`text-xs sm:text-sm text-grey-50 w-full leading-relaxed ${subtitleClassName}`}
                    >
                      {subtitle}
                    </div>
                  )}
                </div>
                {!hideCloseButton && (
                  <button onClick={onClose} className={`absolute right-0 top-0 ${closeButtonClassName}`}>
                    <CloseIcon width={24} height={24} />
                  </button>
                )}
              </header>

              {title && line && <div className="w-full h-px bg-grey-5"></div>}
              <main className="text-base grow overflow-y-auto overflow-x-hidden scrollbar">
                {children}
              </main>
              {footer && (
                <footer className={`flex items-center gap-size1 w-full ${footerClassName}`}>
                  {footer}
                </footer>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // For center modals, use Dialog with backdrop
  return (
    <>
      <Dialog
        open={isOpen}
        onClose={() => closeBackdrop && onClose()}
        className="relative z-50"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 duration-150 bg-[#000]/30 data-[closed]:opacity-0 data-[leave]:delay-100"
        />

        <div className="fixed inset-0 w-screen items-center justify-center flex">
          <DialogPanel
            transition
            className={`${className} transition-all duration-300 w-11/12 data-[closed]:-translate-y-20 data-[closed]:opacity-0 rounded-size1 overflow-hidden ${
              className.includes('max-w-none')
                ? ''
                : `${imageSection ? 'md:max-w-[900px]' : 'md:max-w-[550px]'}`
            } min-w-[350px] md:w-auto max-h-[90vh] flex flex-col border border-grey-5 data-[enter]:delay-100`}
          >
            {hasSideBySideLayout ? (
              <div className={'flex flex-col md:flex-row w-full h-full'}>
                {/* Image Section */}
                <div
                  className={`${imageBgColor} md:w-2/5 flex items-center justify-center py-size3 md:py-size8 px-size5`}
                >
                  {imageSection}
                </div>

                {/* Content Section */}
                <div
                  className={`${contentBgColor} md:w-3/5 flex flex-col gap-size3 p-size3`}
                >
                  <header
                    className={`flex items-start ${headerClassName} relative`}
                  >
                    {backBtn && (
                      <button onClick={() => onBack()} className="w-[30px]">
                        <ChevronLeftIcon />
                      </button>
                    )}
                    <div
                      className={`flex flex-col gap-size1 sm:gap-size2 ${
                        title ? 'max-w-[85%] sm:max-w-[80%]' : 'max-w-[100%]'
                      }`}
                    >
                      {title && (
                        <DialogTitle
                          className={`text-base sm:text-lg font-medium leading-tight sm:leading-snug ${titleClassName}`}
                        >
                          {title}
                        </DialogTitle>
                      )}
                      {subtitle && (
                        <DialogTitle
                          className={`text-xs sm:text-sm text-grey-50 w-full leading-relaxed ${subtitleClassName}`}
                        >
                          {subtitle}
                        </DialogTitle>
                      )}
                    </div>
                    {!hideCloseButton && (
                      <CloseButton className="absolute right-0 top-[-2px]">
                        <CloseIcon width={24} height={24} />
                      </CloseButton>
                    )}
                  </header>

                  {title && line && (
                    <div className="w-full h-px bg-grey-5"></div>
                  )}
                  <main className="text-base grow overflow-y-auto overflow-x-hidden scrollbar">
                    {children}
                  </main>
                  {footer && (
                    <footer className="flex items-center gap-size1 w-full">
                      {footer}
                    </footer>
                  )}
                </div>
              </div>
            ) : (
              // Original layout when no image section is provided
              <div
                className={`${contentBgColor} flex flex-col ${contentGap} ${contentPadding} w-full`}
              >
                <header
                  className={`flex items-center ${headerClassName} relative`}
                >
                  {backBtn && (
                    <button onClick={() => onBack()} className="w-[30px]">
                      <ChevronLeftIcon />
                    </button>
                  )}
                  <div
                    className={`flex flex-col gap-size1 sm:gap-size2 ${
                      title ? 'max-w-[85%] sm:max-w-[80%]' : 'max-w-[100%]'
                    }`}
                  >
                    {title && (
                      <DialogTitle
                        className={`text-base sm:text-lg font-medium leading-tight sm:leading-snug ${titleClassName}`}
                      >
                        {title}
                      </DialogTitle>
                    )}
                    {subtitle && (
                      <DialogTitle
                        className={`text-xs sm:text-sm text-grey-50 w-full leading-relaxed ${subtitleClassName}`}
                      >
                        {subtitle}
                      </DialogTitle>
                    )}
                  </div>
                  {!hideCloseButton && (
                    <CloseButton className={`absolute right-0 top-0 ${closeButtonClassName}`}>
                      <CloseIcon width={24} height={24} />
                    </CloseButton>
                  )}
                </header>

                {title && line && <div className="w-full h-px bg-grey-5"></div>}
                <main className="text-base grow overflow-y-auto overflow-x-hidden scrollbar">
                  {children}
                </main>
                {footer && (
                  <footer className={`flex items-center gap-size1 w-full ${footerClassName}`}>
                    {footer}
                  </footer>
                )}
              </div>
            )}
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
};
export default DModal;
